import { useQuery } from '@apollo/client';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffect, useRef } from 'react';
import { useMount, useUnmount } from 'react-use';

import { BannerToastType, FCC, bannerToast } from '@eluve/components';
import { SiteBannerTypesEnum, SiteBanners } from '@eluve/graphql-types';
import { useSession, useUserIdFromSession } from '@eluve/session-helpers';

import { SiteBannerMarkdownContent } from './SiteBannerMarkdownContent';
import { getSiteBannersQuery } from './operations';

const SITE_BANNER_LOCAL_STORAGE_KEY = 'dismissedSiteBanners';
const POLL_INTERVAL = 1 * 60 * 1000; // 1 minute in milliseconds

type BannerTypeMapping = Record<SiteBannerTypesEnum, BannerToastType>;

const BannerTypeToToastTypeMapping: BannerTypeMapping = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'error',
} as const;

export const SiteBannerProvider: FCC = ({ children }) => {
  const userId = useUserIdFromSession();
  const session = useSession();

  const shownNotificationsRef = useRef<Set<string>>(new Set());

  const [dismissedSiteBanners, setDismissedSiteBanners] = useLocalStorage<
    string[]
  >(SITE_BANNER_LOCAL_STORAGE_KEY, []);

  const handleDismiss = (id: string) => () => {
    const existingDissmissedBanners = JSON.parse(
      localStorage.getItem(SITE_BANNER_LOCAL_STORAGE_KEY) ?? '[]',
    );
    setDismissedSiteBanners([...existingDissmissedBanners, id]);
  };

  const handleShowNotification = (item: SiteBanners) => {
    if (dismissedSiteBanners.includes(item.id)) return;

    bannerToast[BannerTypeToToastTypeMapping[item.bannerType]](item.title, {
      description: <SiteBannerMarkdownContent message={item.message} />,
      onDismiss: handleDismiss(item.id),
    });
  };

  const siteBanners = useQuery(getSiteBannersQuery, {
    skip: !session || !userId,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!data?.siteBanners.length) return;

      const newNotifications = data.siteBanners.filter(
        (item) => !shownNotificationsRef.current.has(item.id),
      );

      newNotifications.forEach((item) => {
        shownNotificationsRef.current.add(item.id);
        handleShowNotification(item as SiteBanners);
      });
    },
  });

  useEffect(() => {
    // Stop polling if there's no valid session
    if (!session || !userId) {
      siteBanners.stopPolling();
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        siteBanners.stopPolling();
      } else {
        siteBanners.startPolling(POLL_INTERVAL);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function will be called when session/userId changes or component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session, userId, siteBanners]);

  useMount(() => {
    // Workaround to get polling work with onCompleted.
    // Typically, onCompleted gets fired once, however,
    // here we set `notifyOnNetworkStatusChange` to true
    // to ensure onCompleted gets triggered.
    siteBanners.startPolling(POLL_INTERVAL);
  });

  useUnmount(() => {
    shownNotificationsRef.current.clear();
    siteBanners.stopPolling();
  });

  return children;
};
